import { DeleteOutline, DragIndicator, Edit } from "@mui/icons-material";
import {
  Box,
  Card,
  Chip,
  IconButton,
  SxProps,
  Typography,
} from "@mui/material";
import ConfirmationPopper from "components/ConfirmationPopper";
import useDraggable from "hooks/DragAndDrop/useDraggable";
import borders from "theme/base/borders";
import colors from "theme/base/colors";
import QuestionDto from "types/QuestionDto";
import useScriptManagementStore from "../../hooks/useScriptManagementStore";
import { QUESTION_DND_TYPE } from "../ScriptQuestions";
import useFormHelpers from "../hooks/useFormHelpers";

const labels = {
  VIDEO: "Video Question",
  MESSAGE: "Message",
  TEXT: "Text Answer",
  NUMERIC: "Numeric Answer",
  SINGLE_CHOICE: "Single Choice",
  MULTIPLE_CHOICE: "Multiple Choice",
  WELCOME: "Welcome Message",
  CONSENT: "Consent Question",
  THANKYOU: "Thank You Message",
};

interface Props {
  question: QuestionDto;
  index: number;
  isDraggable: boolean;
  isEditable: boolean;
  sx?: SxProps;
}

export default function Question({
  question,
  index,
  isDraggable,
  isEditable,
  sx,
}: Props) {
  const { title, type, options } = question;
  const { isOptionType } = useFormHelpers();
  const { handleDeleteQuestion, handleStoreChange } =
    useScriptManagementStore();

  const draggableProps = useDraggable({
    dndType: QUESTION_DND_TYPE,
    index,
    item: question,
  });

  return (
    <Card
      ref={isDraggable ? draggableProps.ref : null}
      sx={{
        position: "relative",
        p: 1,
        pl: !isDraggable ? 3 : 0,
        backgroundColor: !isDraggable ? colors.grey[200] : "common.white",
        cursor: !isDraggable
          ? "pointer"
          : draggableProps.isDragging
            ? "grabbing"
            : "grab",
        borderRadius: borders.borderRadius.md,
        border: `1px solid ${borders.borderColor}`,
        display: "flex",
        flexDirection: "row",
        alignItems: "flex-start",
        span: {
          flex: 1,
        },
        boxShadow: "none",
        ...sx,
      }}
    >
      {isDraggable && (
        <IconButton size="small">
          <DragIndicator />
        </IconButton>
      )}
      <span>
        <Typography variant="subtitle1">{labels[type]}</Typography>
        <Typography gutterBottom>{title}</Typography>
        {isOptionType(type) &&
          options &&
          options.length > 0 &&
          options.map((option, ix) => {
            return (
              <Chip key={`${ix}-${option}`} label={option} sx={{ mr: 1 }} />
            );
          })}
      </span>
      <Box
        sx={({ functions }) => ({
          width: functions.pxToRem(56),
          display: "flex",
          justifyContent: "flex-end",
        })}
      >
        {isDraggable && (
          <ConfirmationPopper
            onConfirmHandler={() => handleDeleteQuestion(index)}
            popperContent={<Typography>Delete this question?</Typography>}
          >
            <IconButton size="small" color="error">
              <DeleteOutline />
            </IconButton>
          </ConfirmationPopper>
        )}
        {isEditable && (
          <IconButton
            size="small"
            color="primary"
            onClick={() => {
              handleStoreChange("isEditing", true);
              handleStoreChange("editIndex", index);
            }}
          >
            <Edit />
          </IconButton>
        )}
      </Box>
      <Typography
        component="em"
        sx={{ position: "absolute", right: 8, bottom: 8 }}
      >
        {index + 1}
      </Typography>
    </Card>
  );
}
